import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';

import { Button } from '$partials/ui';
import { Dispatcher as IconDispatcher } from '$partials/icon';
import { useLanguage } from '$contexts/language.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const VideoLocal = ({
    autoplay = null,
    file     = null,
    loop     = null,
}) => {
    // references
    const videoRef = useRef();

    // state
    const [isPlaying, setIsPlaying] = useState(autoplay ? true : false);
    const [showControls, setShowControls] = useState(false);

    // methods
    const handleClick = () => {
        if (videoRef.current) {
            videoRef.current.play();
            setShowControls(true);
        }
    };

    const handleVideoEvent = (_isPlaying) => {
        if (videoRef.current) {
            if (_isPlaying) {
                videoRef.current.play();
                setIsPlaying(true);
                setShowControls(true);
            } else {
                setIsPlaying(false);
                setShowControls(false);
            }
        }
    };

    // hooks
    const { translate: t } = useLanguage();

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.addEventListener('play', handleVideoEvent.bind(this, true), { passive: true });
            videoRef.current.addEventListener('ended', handleVideoEvent.bind(this, false), { passive: true });
        }

        return () => {
            if (videoRef.current) {
                videoRef.current.removeEventListener('play', handleVideoEvent);
                videoRef.current.removeEventListener('ended', handleVideoEvent);
            }
        };
    }, [videoRef]);

    // render
    return (
        <section className='module-video-local'>
            <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                <div className='flex flex-wrap'>
                    {/* side title */}
                    <div className='absolute top-0 md-relative md-col-2' />

                    {/* content */}
                    <div className='col-12 sm-col-12 md-col-10'>
                        <div className='ml9 sm-ml9 md-ml0'>
                            <div className='px-gutter'>
                                {file.url && (
                                    <figure>
                                        <div className='relative font-0 border-rad-10'>
                                            <video
                                                autoPlay={autoplay}
                                                className='width-100'
                                                controls={autoplay ? false : showControls}
                                                disablePictureInPicture={false}
                                                loop={loop}
                                                muted={autoplay ? true : false}
                                                playsInline={true}
                                                ref={videoRef}
                                                src={file.url} />

                                            <Button
                                                action={handleClick}
                                                className='form-field-reset expand-interaction__action video-player-button'
                                                hidden={isPlaying}>
                                                <span aria-label={t('Play video')}>
                                                    <IconDispatcher name='IconPlay' />
                                                </span>
                                            </Button>
                                        </div>
                                    </figure>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    autoplay: PropTypes.bool,
    file    : PropTypes.object.isRequired,
    loop    : PropTypes.bool,
};

VideoLocal.propTypes = propTypes;

export { VideoLocal };

export default VideoLocal;
